import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS } from 'react-joyride';
import { useColorModeValue } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translate } from '../../../i18n';
import { UserRole } from '../../../models/organization/dto/invite-to-organization.dto';
import { useAuth } from '../../auth/AuthProvider';
import { useUserRole } from '../../auth/roles';
import { useGetCurrentAuthenticatedCoach } from '../../coach-management/hooks/useGetCurrentAuthenticatedCoach';
import { useGetMyOrganizations } from '../../organization/hooks/useGetMyOrganizations';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useCoachTourStore } from '../hooks/useCoachTourStore';
import { useCompleteCoachTour } from '../hooks/useCompleteCoachTour';
import { useSeenTours } from '../hooks/useSeenTours';
import {
  COACH_ONBOARDING_TOUR_ID,
  CoachTourStep,
  coachOnboardingSteps,
  shouldAutoLaunchCoachTour,
} from '../tours/coach-onboarding-tour';

/** Does the current location satisfy a step's route (pathname + query params)? */
function locationMatchesRoute(
  route: string,
  location: { pathname: string; search: string },
): boolean {
  const [pathname, search = ''] = route.split('?');
  if (location.pathname !== pathname) return false;
  const wanted = new URLSearchParams(search);
  const actual = new URLSearchParams(location.search);
  return Array.from(wanted.entries()).every(([k, v]) => actual.get(k) === v);
}

/** Poll for a step's target so Joyride opens on a mounted element. */
function waitForTarget(selector: string, timeoutMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const tick = () => {
      if (document.querySelector(selector)) return resolve(true);
      if (Date.now() - startedAt > timeoutMs) return resolve(false);
      setTimeout(tick, 120);
    };
    tick();
  });
}

/**
 * MYS-132 — the coach first-login tutorial. Mounted ONCE in AdminLayout (above
 * the routed pages) so it survives navigation: unlike GuidedTour, this tour
 * spans several routes, navigating to each step's page before spotlighting it.
 *
 * Auto-launch is deliberately conservative: only after `my-organizations` has
 * resolved (the role fails open to ADMIN before that), only for a COACH, and
 * only when no organization of theirs carries a completion timestamp — see
 * shouldAutoLaunchCoachTour. Finishing, skipping, or closing the tour stamps
 * the membership server-side and the uid-scoped localStorage flag locally.
 * The navbar avatar menu can request a replay at any time (useCoachTourStore).
 */
export default function CoachOnboardingTour() {
  const { currentUser } = useAuth();
  const uid = currentUser?.sub as string | undefined;
  const role = useUserRole();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasSeen, markSeen } = useSeenTours(uid);
  const { selectedOrganization } = useSelectedOrganization();
  const { data: organizations, isPlaceholderData } = useGetMyOrganizations(
    Boolean(currentUser),
  );
  const isCoach = role === UserRole.COACH;
  const coachQuery = useGetCurrentAuthenticatedCoach(
    isCoach ? selectedOrganization?.id : undefined,
  );
  const completeCoachTour = useCompleteCoachTour();
  const replayRequested = useCoachTourStore.use.replayRequested();
  const clearReplayRequest = useCoachTourStore.use.clearReplayRequest();

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  // One auto-launch per session: after finishing, the localStorage flag and the
  // server timestamp keep it down, but neither lands synchronously.
  const launchedRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const steps: CoachTourStep[] = useMemo(
    () => coachOnboardingSteps(coachQuery.data?.id),
    [coachQuery.data?.id],
  );
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const locationRef = useRef(location);
  locationRef.current = location;

  const goToStep = useCallback(
    async (index: number) => {
      const step = stepsRef.current[index];
      if (!step) return;
      if (!locationMatchesRoute(step.route, locationRef.current)) {
        // Hide the tooltip while the page changes, then re-open on the new
        // route once the target exists (or let TARGET_NOT_FOUND skip it).
        setRun(false);
        navigate(step.route);
      }
      await waitForTarget(step.target as string);
      if (!mountedRef.current) return;
      setStepIndex(index);
      setRun(true);
    },
    [navigate],
  );

  const start = useCallback(() => {
    launchedRef.current = true;
    clearReplayRequest();
    setRun(false);
    setStepIndex(0);
    void goToStep(0);
  }, [clearReplayRequest, goToStep]);

  const finish = useCallback(() => {
    setRun(false);
    setStepIndex(0);
    markSeen(COACH_ONBOARDING_TOUR_ID);
    if (selectedOrganization?.id) {
      completeCoachTour.mutate(selectedOrganization.id);
    }
  }, [markSeen, selectedOrganization?.id, completeCoachTour]);

  // Auto-launch on first sign-in. Waiting for the coach query to settle means
  // the "your profile / availabilities" steps are included whenever the coach
  // row exists; on error the tour still runs with the route-independent steps.
  const organizationsSettled = !isPlaceholderData;
  const coachSettled = coachQuery.isFetched || coachQuery.isError;
  useEffect(() => {
    if (launchedRef.current || run || replayRequested) return;
    if (!organizationsSettled || !coachSettled) return;
    const autoLaunch = shouldAutoLaunchCoachTour({
      role,
      organizations,
      seenLocally: hasSeen(COACH_ONBOARDING_TOUR_ID),
    });
    if (!autoLaunch) return;
    // Let the landing page paint before the spotlight appears.
    const timer = setTimeout(start, 800);
    return () => clearTimeout(timer);
  }, [
    organizationsSettled,
    coachSettled,
    organizations,
    role,
    run,
    replayRequested,
    hasSeen,
    start,
  ]);

  // Replay from the navbar menu — bypasses every "already seen" flag.
  useEffect(() => {
    if (replayRequested && !run) start();
  }, [replayRequested, run, start]);

  const handleCallback = (data: CallBackProps) => {
    const { status, action, index, type } = data;
    if (
      status === STATUS.FINISHED ||
      status === STATUS.SKIPPED ||
      action === ACTIONS.CLOSE
    ) {
      finish();
      return;
    }
    if (type === EVENTS.TARGET_NOT_FOUND) {
      if (index < stepsRef.current.length - 1) {
        void goToStep(index + 1);
      } else {
        finish();
      }
      return;
    }
    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        void goToStep(index + 1);
      } else if (action === ACTIONS.PREV) {
        void goToStep(index - 1);
      }
    }
  };

  const bgColor = useColorModeValue('#FFFFFF', '#1B2559');
  const textColor = useColorModeValue('#1B2559', '#FFFFFF');
  const primaryColor = useColorModeValue('#422AFB', '#7551FF');
  const styles = {
    options: {
      arrowColor: bgColor,
      backgroundColor: bgColor,
      primaryColor,
      textColor,
      zIndex: 10000,
    },
    tooltip: {
      borderRadius: '16px',
      padding: '20px',
      fontSize: '14px',
    },
    tooltipTitle: {
      fontSize: '16px',
      fontWeight: 700,
    },
    buttonNext: {
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 600,
      padding: '8px 16px',
    },
    buttonBack: {
      color: textColor,
      fontSize: '13px',
      fontWeight: 600,
    },
    buttonSkip: {
      color: textColor,
      fontSize: '13px',
    },
  };

  if (!isCoach || steps.length === 0) return null;

  return (
    <Joyride
      steps={steps}
      stepIndex={stepIndex}
      run={run}
      continuous
      showSkipButton
      showProgress
      disableScrolling={false}
      callback={handleCallback}
      styles={styles}
      locale={{
        back: translate('walkthrough.tour_back'),
        close: translate('walkthrough.tour_close'),
        last: translate('walkthrough.tour_last'),
        next: translate('walkthrough.tour_next'),
        nextLabelWithProgress: translate('walkthrough.tour_next_with_progress'),
        skip: translate('walkthrough.tour_skip'),
      }}
      floaterProps={{
        disableAnimation: true,
      }}
    />
  );
}
